import { Helmet } from "react-helmet";
import Selector from "../../../components/Selector/Selector";
import WebPushReportCard from "../../../components/Cards/WebPushReportCard/WebPushReportCard";
import "./CampaignReports.css";
import { t } from "i18next";
import { CampaignChannelTypeEnum } from "utils/enums";
import { DateRangeData } from "@customTypes/dateRange"
import { useEffect, useMemo, useState } from "react";
import { DateRangeSelector } from "components/DateTimeSelectors/DateRangeSelector/DateRangeSelector";
import centralApi from "services/centralApi";
import { API_ENDPOINTS, ChannelTypeEnum } from "utils/constants";
import { useDispatch, useSelector } from "react-redux";
import { BOOLEAN_OPTIONS } from "utils/constants/selectorOptions";
import { formatDate } from "helperFunctions/date";
import Spinner from "components/Spinner/Spinner";
import CampaignPerformance from "./PerformanceChart/PerformanceChart";
import CustomTable from "components/CustomTable/CustomTable";
import Search from "components/Inputs/Search/Search";
import {  PAGE_OPTIONS, TABLE_OPTIONS } from "utils/constants/selectorOptions"
import { generateNumbersInRange } from "helperFunctions/number";
import useDebouncedValue from "hooks/useDebouncedState";
import { getToken } from "redux/features/app/authTokenSlice";
import useMessage from "hooks/useMessage";
import { CopyTwoTone } from "assets/icons";
import { Dropdown } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { copyTextToClipboard } from "helperFunctions/common";
import AccessControl from "components/HOC/AccessControl/AccessControl";
import { roleCheckTypes } from "utils/constants/constants";

export const CampaignReports = () => {
  const [selectedChannel, setSelectedChannel] =useState<string>( CampaignChannelTypeEnum.ALL);
  const [selectedAuditChannel, setSelectedAuditChannel] =useState<string>( CampaignChannelTypeEnum.ALL);
  const {  showSuccess } = useMessage();
  const [dateRange, setDateRange] = useState<DateRangeData | null>(null);
  const [campaignPerformance ,setCampaignPerformance] = useState<any>();
  const [loading, setLoading] = useState(BOOLEAN_OPTIONS.FALSE)
  const [loadingTable, setLoadingTable] = useState(BOOLEAN_OPTIONS.FALSE)

  const loginUser = useSelector((state: any) => state.loginUser)
  const { appId } = useSelector((state: any) => state.activeApp)
  const [pageData, setPageData] = useState(PAGE_OPTIONS.PAGINATION)

  const [search, setSearch] = useDebouncedValue("", 500)

   // Fetching channel status from redux store
   const features = useSelector((state: any) => state.featuresSetting.data);
   const channelStatus = features[0]?.channel_status;
   const dispatch : any = useDispatch()
   const [tableData, setTableData] = useState([])
   const [totalEntries, setTotalEntries] = useState(0)
  
   // Memoize the options generation for performance
   const options = useMemo(() => {
     // Pre-generate a map of campaign channel enum types for easier access
     const allChannels = Object.values(CampaignChannelTypeEnum);
     
     // If channel_status is undefined, return all enum channels
     if (!channelStatus) {
       return allChannels.map((key) => ({
         value: key,
         label: key,
       }));
     }
 
     // Otherwise, filter the channels where the status is true
     return allChannels
       .filter((key) => key == CampaignChannelTypeEnum.ALL || channelStatus[key] === true) // Only include enabled channels
       .map((key) => ({
         value: key,
         label: key,
       }));
   }, [channelStatus]); 

  
    //get the campaign reports from api and set the state
    async function getCampaignReports() {
      try {
        const apiKey = loginUser.data.api_key
        if (apiKey && appId) {
          setLoading(BOOLEAN_OPTIONS.TRUE)
          const params= {
          app_id: appId,
          api_key: apiKey,
          sd:dateRange?.startDate_C ? dateRange?.startDate_C * 1000 : undefined,
          ed:dateRange?.endDate_C ?  dateRange?.endDate_C * 1000 : undefined,
          channelType:selectedChannel == CampaignChannelTypeEnum.ALL ? undefined : selectedChannel,
        }
          
        const data =  await centralApi("GET",API_ENDPOINTS.GET_CAMPAIGNS_PERFORMANCE_REPORTS , null, params)
        setCampaignPerformance(data)
        setLoading(BOOLEAN_OPTIONS.FALSE)
        
        }
      } catch (error) {
        setLoading(BOOLEAN_OPTIONS.FALSE)
      }
    }

  const handleDateRange = (dateRangeData: any) => {
    setDateRange(dateRangeData);
  };
   const onPageChange = (value: any) => {
    setPageData(value)
  }
  const columns: any = [
    {
      title: t("userIdentityLabel"),
      key: "userIdentity",
      width: 350,
      render: (details: any) => {
      
        return (
          <div className="d-flex justify-content-between align-items-start" >
          <div className="d-flex flex-column">
           {details.id}
          {details.did? 
           <span className="text-disabled d-flex align-items-center">
          {t("didLabel")}: {details.did}
 
           </span>:null}

          </div>
          
        </div>
        );
      },
    },

  {
    title: t("campaignLabel"),
    key: "name",
    width: 350,
    render: (details: any) => {
      const content = details.content;
  
      const fields = [
        { key: "subject", label: t("subjectLabel") },
        { key: "title", label: t("titleLabel") },
        { key: "body", label: t("bodyLabel") },
        { key: "end", label: t("expandedTextDescriptionLabel") },
     
      ];
  
      const message = (
        <div className="template-dropdown-content">
          {fields.map(({ key, label }) =>
            content?.[key] ? (
              <div key={key}>
                <b>{label}:</b> {content[key]}
              </div>
            ) : null
          )}
        </div>
      );
  
      return (
        <div className="d-flex justify-content-between align-items-start">
          <div className="d-flex flex-column">
            {details.name}
            <span className="text-disabled d-flex align-items-center">
              {details.campId}
              <CopyTwoTone
                className="ms-2 cursor-pointer"
                onClick={() => {
                  copyTextToClipboard(details.campId);
                  showSuccess(t("copiedToClipboardLabel"));
                }}
              />
            </span>
          </div>
  
          <Dropdown
            menu={{ items: [{ key: "1", label: message }] }}
            trigger={["hover"]}
            className="mt-2"

          >
            <InfoCircleOutlined className="cursor-pointer" />
          </Dropdown>
        </div>
      );
    },
  },
    {
      title: t("typeLabel"),
      dataIndex: "type",
      key: "type",
    },
  
    {
      title: t("statusLabel"),
      dataIndex: "receivedTo",
      key: "receivedTo",
      className: "text-start",
      render: (value: any, record: any) => {
        if (value) return <div>{t("successLabel")}</div>;
    
        const message = record?.errorMessage ;
    
        return (
          <div className="d-flex align-items-center">
            <span className=" me-2">{t("failedLabel")}</span>
    
            <Dropdown
              menu={{ items: [{ key: "1", label: message }] }}
              trigger={["hover"]}
            >
              <InfoCircleOutlined className="cursor-pointer" />
            </Dropdown>
          </div>
        );
      },
    },

    {
      title: t("sentAtLabel"),
      dataIndex: "receivedAt",
      key: "receivedAt",
      className: "text-center",
      render: (sd: number | null) => (
        <div>
          {sd ? formatDate(sd, "DD-MM-YYYY hh:mm:ss A") : "--"}
        </div>
      ),
      
    },
  ];
  async function getCampaignAuditTrail() {
    try {
      const apiKey = loginUser.data.api_key
  
      if (apiKey && appId) {
        setLoadingTable(true)
        dispatch(getToken())
  
        let params: Record<string, string | number> = {
          app_id: appId,
          api_key: apiKey,
          page: pageData.current,
          limit: pageData.pageSize,
        }
  
        if (search?.trim()) {
          params.search = search.trim()
        }
  
        if (selectedAuditChannel !== CampaignChannelTypeEnum.ALL) {
          params.channel = selectedAuditChannel
        }
  
        const response = await centralApi(
          "GET",
          API_ENDPOINTS.GET_CAMPAIGN_AUDIT_REPORT,
          null,
          params
        )
  
        setTableData(response.data)
        setTotalEntries(response.totalCount)
        setLoadingTable(false)
      }
    } catch (error) {
      setLoadingTable(false)
    }
  }
  
  

  useEffect(()=>{
    if(dateRange){
    getCampaignReports();
    }
  },[dateRange,selectedChannel]);
  useEffect(() => {
    getCampaignAuditTrail()
  }, [search,selectedAuditChannel,pageData.pageSize,pageData.current])
  

  return (
    <div className="container-fluid">    
      <div className="web-push-reports-container">
        <Helmet>
          <title>Appice | Campaign Reports</title>
        </Helmet>
        <div className="w-100 h-100 d-flex justify-content-between flex-wrap my-3">
          <div>
          <h4>{t("overviewLabel")}</h4>
          </div>        
          <div>
              <div className=" d-flex gap-1">
              <Selector
                placeholder="Select channel"
                onChange={(value) => setSelectedChannel(value)}
                className="channel-selector-width"
                value={selectedChannel}
                showSearch
                options={options}
              />
            <div>
            <strong>{t("periodLabel")} </strong>
              <DateRangeSelector
              className="w-75"
              onDateChange={handleDateRange}
              disableRange={{ range: 1, rangeType: "year" }}
              disableType="years"

            />
           </div>
         </div>
          </div>
        </div>
        {loading ?  (
          <div className="d-flex justify-content-center my-5">
            <Spinner />
          </div>
        ) : (
        <>
        <div className="web-push-reports-cards">
          <WebPushReportCard 
            heading={t("reachableAudienceLabel")} 
            number={campaignPerformance?.reachCount} 
            percentageTwo={campaignPerformance?.reachDiffPercentage}
            timeString={`w.r.t. ${formatDate(
              dateRange?.startDate_P,
              "D MMM, YY"
            )} - ${formatDate(dateRange?.endDate_P, "D MMM, YY")}`}
            />
          <WebPushReportCard
            type="sent"
            heading={t("sentLabel")}
            number={campaignPerformance?.sentCount}
            percentageTwo={campaignPerformance?.sentDiffPercentage}
            timeString={`w.r.t. ${formatDate(
              dateRange?.startDate_P,
              "D MMM, YY"
            )} - ${formatDate(dateRange?.endDate_P, "D MMM, YY")}`}
          />
          <WebPushReportCard
          type="double"
            heading={t("clicksLabel")}
            headingTwo={t("CTRLabel")}
            number={campaignPerformance?.clicksCount}
            headingTwoPercentage={campaignPerformance?.ctrPercentage}
            percentageTwo={campaignPerformance?.clicksDiffPercentage}
            timeString={`w.r.t. ${formatDate(
              dateRange?.startDate_P,
              "D MMM, YY"
            )} - ${formatDate(dateRange?.endDate_P, "D MMM, YY")}`}
          />
          <WebPushReportCard
            heading={t("ConversionLabel")}
            number={campaignPerformance?.conversionCount}
            percentageOne={campaignPerformance?.conversionPercentage}
            percentageTwo={campaignPerformance?.conversionDiffPercentage}
            timeString={`w.r.t. ${formatDate(
              dateRange?.startDate_P,
              "D MMM, YY"
            )} - ${formatDate(dateRange?.endDate_P, "D MMM, YY")}`}
          />
        </div>       
        <div className="mt-3 mb-1">
          <h6>{t("campaignPerformanceLabel")}</h6>
        </div>
        <div className="web-push-reports-campaigns">
            <CampaignPerformance data={campaignPerformance?.campaignPerformanceDayWise} />
        </div>
        </>
        )}
      </div>
      <AccessControl accessControl={{role:[roleCheckTypes.SuperUser ]}}> 

      <div className="p-4 mt-4 bg-white">
    <h6>{t("campaignAuditTrailLabel")}</h6>
   <div className=" d-flex gap-2 h-100 flex-wrap">
              <Search
                placeholder="Search"
                  onChange={(e: any) => {
                    setSearch(e.target.value)
                    setPageData(PAGE_OPTIONS.PAGINATION);
                  }}
                size="middle"
                className="w-25"
                disabled={loadingTable} 
              />
           
                <Selector
                  onChange={(value) => {
                    setPageData({ ...pageData, pageSize: value })
                  }}

                  defaultValue={PAGE_OPTIONS.DEFAULT_VALUE}
                  size="middle"
                  options={generateNumbersInRange(1, 50).map((num) => ({
                    label: num,
                    value: num,
                  }))}
                  allowManualInput={true}
                  value={pageData.pageSize}
                  disabled={loadingTable} 
                                  />
               <Selector
                placeholder="Select channel"
                onChange={(value) => {
                  setSelectedAuditChannel(value);
                  setPageData(PAGE_OPTIONS.PAGINATION);
                }}
                disabled={loadingTable}             
                style={{ width: 150 }}
                value={selectedAuditChannel}
                showSearch
                options={
                  [
                  {label:t("allLabel"),
                  value:CampaignChannelTypeEnum.ALL},
                  {label:t("pushLabel"),
                  value:ChannelTypeEnum.PUSH},
                  {label:t("smsLabel"),
                  value:ChannelTypeEnum.SMS},
                  {label:t("inappLabel"),
                  value:ChannelTypeEnum.INAPP},
                  {label:t("emailLabel"),
                  value:ChannelTypeEnum.EMAIL},
                  {label:t("webPushLabel"),
                  value:ChannelTypeEnum.WEBPUSH}
                ]

                }
              />

            </div>
            <div className="mt-2">
                        <CustomTable
            columns={columns}
            dataSource={tableData?.map((campaign: any) => ({
              ...campaign,
              key: campaign._id,
            }))}
            size={TABLE_OPTIONS.SIZE}
            loading={loadingTable}
            onChange={onPageChange}
            pagination={{
              current: pageData.current,
              pageSize: pageData.pageSize,
              simple: pageData.simple,
              total: totalEntries,
            }}
            scroll={{ x: 900 }}

          />
          </div>

   </div>
   </AccessControl>
    </div>
  );
};
